<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0 resource-title">Topics</h5>
          <small class="text-muted">Manage planner topics linked to concepts</small>
        </div>
      </div>

      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadTopics"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row topics-planner-filters mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div class="filter-fields-group filter-fields-group--cols-4 d-flex align-items-center">
            <b-form-input
              v-model="searchQuery"
              class="filter-search-input"
              :placeholder="$t('Search') + '...'"
              @input="onSearchChange"
            />
            <v-select
              v-model="filters.classID"
              :options="classes"
              :clearable="true"
              label="name"
              :reduce="(val) => val.id"
              placeholder="Class"
              class="filter-control filter-field-item topics-planner-filter-select planner-filter-select"
              @input="onClassChange"
            />
            <v-select
              v-model="filters.subjectID"
              :options="subjects"
              :clearable="true"
              label="subject"
              :reduce="(val) => val.id"
              placeholder="Subject"
              :disabled="isTeacherScoped && !filters.classID"
              class="filter-control filter-field-item topics-planner-filter-select planner-filter-select"
              @input="onFilterChange"
            />
            <v-select
              v-model="filters.conceptID"
              :options="filteredConcepts"
              :clearable="true"
              label="concept"
              :reduce="(val) => val.id"
              placeholder="Concept"
              class="filter-control filter-field-item topics-planner-filter-select planner-filter-select"
              @input="onFilterChange"
            />
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="openAddModal"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>Add Topic</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table mt-1"
          :items="topics"
          :fields="fields"
          :busy="dataLoading"
          show-empty
          :responsive="false"
          hover
          small
        >
          <template #empty>
            <h3 v-if="!dataLoading" class="mt-1 mb-1 text-center">
              {{ $t("No records found") }}
            </h3>
          </template>

          <template #table-busy>
            <div class="list-skeleton">
              <div
                v-for="n in 6"
                :key="'skeleton-' + n"
                class="list-skeleton__row"
                :style="{
                  gridTemplateColumns: `repeat(${fields.length}, minmax(0, 1fr))`,
                }"
              >
                <div
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + n + '-' + index"
                  class="list-skeleton__cell"
                >
                  <div
                    class="list-skeleton__bar"
                    :style="{ width: skeletonBarWidth(index) }"
                  ></div>
                </div>
              </div>
            </div>
          </template>

          <template #cell(index)="data">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
          </template>

          <template #cell(topic)="data">
            <b-badge variant="light-warning">
              {{ data.value }}
            </b-badge>
          </template>

          <template #cell(className)="data">
            <b-badge variant="light-primary">
              {{ data.value }}
            </b-badge>
          </template>

          <template #cell(subjectName)="data">
            <b-badge variant="light-success">
              {{ data.value }}
            </b-badge>
          </template>

          <template #cell(conceptName)="data">
            <b-badge variant="light-info">
              {{ data.value }}
            </b-badge>
          </template>

          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex justify-content-center">
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                title="Edit"
                class="action-btn-table mr-1"
                @click="editTopic(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>

              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.top
                title="Delete"
                class="action-btn-outline delete-btn"
                @click="deleteTopic(item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Topic' : 'Add Topic'"
      @hidden="resetForm"
      centered
      no-close-on-backdrop
    >
      <b-form @submit.prevent>
        <b-row>
          <b-col md="12">
            <b-form-group label="Concept" label-for="concept">
              <v-select
                v-model="form.conceptID"
                :options="filteredConcepts"
                :clearable="false"
                label="concept"
                :reduce="(val) => val.id"
                placeholder="Select concept"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <b-form-group label="Topic">
              <b-form-input
                v-model.trim="form.mainTopic"
                placeholder="Enter topic name"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" @click="saveTopic" :disabled="loading">
          <b-spinner v-if="loading" small />
          {{ isEdit ? "Update" : "Save" }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BSpinner,
  BBadge,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import Swal from "sweetalert2";
import teacherPlannerScopeMixin from "./teacherPlannerScopeMixin";
import plannerFilterDropdownScrollMixin from "./plannerFilterDropdownScroll";

export default {
  name: "Topics",
  mixins: [teacherPlannerScopeMixin, plannerFilterDropdownScrollMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BSpinner,
    BBadge,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      topics: [],
      allConcepts: [],
      classes: [],
      subjects: [],
      searchQuery: "",
      searchTimeout: null,
      showModal: false,
      isEdit: false,
      loading: false,
      dataLoading: true,
      currentPage: 1,
      perPage: 20,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      fields: [
        { label: "S.No", key: "index", thClass: "text-center", tdClass: "text-center" },
        { key: "topic", label: "Topic" },
        { key: "className", label: "Class" },
        { key: "subjectName", label: "Subject" },
        { key: "conceptName", label: "Concept" },
        { key: "actions", label: "Actions", thClass: "text-center", tdClass: "text-center" },
      ],
      form: {
        id: 0,
        classID: null,
        subjectID: null,
        sectionID: 0,
        conceptID: null,
        mainTopic: "",
        isActive: true,
        campusID: 0,
      },
      filters: {
        classID: null,
        subjectID: null,
        conceptID: null,
      },
    };
  },
  computed: {
    reportDomain() {
      return (this.$store.state.report || "").replace(/_/g, "");
    },
    plannerCampusID() {
      return this.reportDomain === "myskooliuss"
        ? 1
        : this.$store.state.userData.cId;
    },
    displayedRange() {
      if (this.totalCount === 0) {
        return { start: 0, end: 0 };
      }
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    filteredConcepts() {
      return this.filterRowsForTeacherScope(this.allConcepts);
    },
  },
  async created() {
    // Initialize rights from menu and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          assignSections: ch.assignSections,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    await this.initTeacherPlannerScope();
    await this.loadClasses();
    if (!this.isTeacherScoped) {
      await this.loadAllPlannerSubjects();
      this.subjects = this.allSubjects;
    }
    await this.loadAllConcepts();
    await this.loadTopics();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    skeletonBarWidth(index) {
      const widths = ["42%", "72%", "58%", "65%", "80%", "70%", "48%", "40%"];
      return widths[index % widths.length];
    },
    async loadAllConcepts() {
      let url =
        this.$store.state.domain +
        "PlannerConcepts/GetByPagination?db=" +
        this.$store.state.userData.db +
        "&campusID=" +
        this.plannerCampusID +
        "&page=1&pageSize=1000";

      const scopeParts = [];
      this.appendTeacherScopeQueryParts(
        scopeParts,
        this.filters.classID || null
      );
      url += "&" + scopeParts.join("&");
      url +=
        "&subjectID=" + (this.filters.subjectID || "");

      const obj = {
        url,
        token: this.$store.state.userData.token,
        dashboard: true,
      };
      const res = await this.get(obj);
      let concepts = [];
      if (res && Array.isArray(res.data)) {
        concepts = res.data;
      } else if (Array.isArray(res)) {
        concepts = res;
      }
      this.allConcepts = this.filterRowsForTeacherScope(concepts);
    },

    async loadClasses() {
      if (this.isTeacherScoped) {
        this.classes = await this.loadTeacherClasses();
        return;
      }

      const obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.plannerCampusID +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      const classesData = await this.get(obj);
      const list = classesData?.data || classesData || [];
      this.classes = Array.isArray(list)
        ? list.map((el) => el.cls).filter(Boolean)
        : [];
    },

    buildTopicsRequest(page, pageSize) {
      let url =
        this.$store.state.domain +
        "PlannerMainTopics/GetByPagination?db=" +
        this.$store.state.userData.db +
        "&campusID=" +
        this.plannerCampusID +
        "&page=" +
        page +
        "&pageSize=" +
        pageSize;

      const scopeParts = [];
      this.appendTeacherScopeQueryParts(
        scopeParts,
        this.filters.classID || null
      );
      url += "&" + scopeParts.join("&");
      url +=
        "&subjectID=" +
        (this.filters.subjectID || "") +
        "&conceptID=" +
        (this.filters.conceptID || "");
      if (this.searchQuery && this.searchQuery.trim()) {
        url += "&search=" + encodeURIComponent(this.searchQuery.trim());
      }

      return {
        url,
        token: this.$store.state.userData.token,
        dashboard: true,
      };
    },

    async loadTopics(page) {
      if (page) {
        this.currentPage = page;
      }
      this.dataLoading = true;
      try {
        if (this.isTeacherScoped) {
          const cacheKey = [
            this.filters.classID,
            this.filters.subjectID,
            this.filters.conceptID,
            this.searchQuery,
            this.assignedClassIDs.join(","),
          ].join("|");
          const allItems = await this.fetchTeacherScopedPaginatedData({
            cacheKey,
            fetchPage: (p, ps) => this.get(this.buildTopicsRequest(p, ps)),
          });
          const { items, totalCount } = this.getTeacherScopedPageSlice(allItems);
          this.topics = items;
          this.totalCount = totalCount;
        } else {
          const res = await this.get(
            this.buildTopicsRequest(this.currentPage, this.perPage)
          );
          if (res && Array.isArray(res.data)) {
            this.topics = res.data;
            this.totalCount = res.total;
          } else {
            this.topics = [];
            this.totalCount = 0;
          }
        }
      } finally {
        this.dataLoading = false;
      }
    },

    async onClassChange(classID) {
      this.invalidateTeacherScopedCache();
      if (this.isTeacherScoped) {
        this.filters.subjectID = null;
        this.filters.conceptID = null;
      }
      this.subjects = this.updateScopedSubjects(classID);
      this.currentPage = 1;
      await this.loadAllConcepts();
      this.loadTopics();
    },

    async onFilterChange() {
      this.invalidateTeacherScopedCache();
      this.currentPage = 1;
      const prevConceptID = this.filters.conceptID;
      await this.loadAllConcepts();
      if (
        prevConceptID &&
        !this.allConcepts.some((c) => c.id === prevConceptID)
      ) {
        this.filters.conceptID = null;
      }
      this.loadTopics();
    },

    onSearchChange() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.invalidateTeacherScopedCache();
        this.currentPage = 1;
        this.loadTopics();
      }, 500);
    },

    openAddModal() {
      this.isEdit = false;
      this.showModal = true;
    },

    editTopic(item) {
      this.isEdit = true;
      this.form = {
        ...item,
        mainTopic: item.topic || item.mainTopic || "", // Map topic to mainTopic for editing
      };
      this.showModal = true;
    },

    async deleteTopic(item) {
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        const obj = {
          url:
            this.$store.state.domain +
            "PlannerMainTopics/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Topic deleted successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        const success = await this.delete(obj);
        if (success) {
          this.loadTopics();
        }
      }
    },

    async saveTopic() {
      if (!this.form.conceptID || !this.form.mainTopic.trim()) {
        this.$bvToast.toast("Please fill all required fields!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      // Find selected concept and set classID and subjectID
      const selectedConcept = this.allConcepts.find(
        (concept) => concept.id === this.form.conceptID
      );
      if (selectedConcept) {
        this.form.classID = selectedConcept.classID;
        this.form.subjectID = selectedConcept.subjectID;
      }

      this.loading = true;
      this.form.campusID = this.plannerCampusID;

      const obj = {
        url:
          this.$store.state.domain +
          "PlannerMainTopics/save?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          this.plannerCampusID +
          "&userID=" +
          this.getCurrentUserLinkID(),
        body: this.form,
        message:
          "Topic " + (this.isEdit ? "updated" : "created") + " successfully.",
        context: this,
        token: this.$store.state.userData.token,
      };

      const success = await this.post(obj);

      if (success) {
        this.showModal = false;
        this.loadTopics();
      }
      this.loading = false;
    },

    resetForm() {
      this.form = {
        id: 0,
        classID: null,
        subjectID: null,
        sectionID: 0,
        conceptID: null,
        mainTopic: "",
        isActive: true,
        campusID: this.plannerCampusID,
      };
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadTopics();
    },
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "./plannerListTable.scss";

</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

@import "./plannerFilterDropdownMenu.scss";
</style>
